# Idempotency

N4 guarantees that retrying a mutating request returns the same response without creating a
duplicate. Two layers:

## 1. Header-driven (recommended)

Pass `X-N4-Idempotency-Key` with any UUID-like string you generate. N4 keys the dedupe window
on `(store_id, idempotency_key)`:

```bash
curl -u "$PUB:$SEC" \
  -H 'X-N4-Idempotency-Key: 33b6e0e0-1234-4f7d-bb45-...' \
  -H 'Content-Type: application/json' \
  -X POST https://api.n4-payments.com.br/api/v1/transactions \
  -d '{ /* ... */ }'
```

Outcomes:

| Scenario | Result |
|---|---|
| First call | Processed normally; response cached |
| Same key + same body | Returns the original response (200) — safe to retry |
| Same key + different body | `409 idempotency_conflict` — your client has a bug; investigate |
| Same key + still processing | `409 idempotency_in_flight` — wait and retry |

TTL: 24h for completed responses, 1h for in-flight locks.

## 2. Auto-derived (when you don't send the header)

If you omit `X-N4-Idempotency-Key`, N4 derives one from `(store_id, externalRef, paymentMethod, amount)`.
This is enough to dedupe well-behaved clients that already send a unique `externalRef` per logical
transaction. Two retries that hit it within 24h still collapse.

Caveat: if `externalRef` is null, the derived key is weaker (only `paymentMethod + amount`); two
unrelated payments of the same amount/method from the same store within 24h collide. Always send
either the header or an `externalRef`.

## Recommended pattern

```javascript
async function createPayment(order) {
  // Generate ONCE per attempt; persist on the order row before sending.
  if (!order.idempotencyKey) {
    order.idempotencyKey = crypto.randomUUID();
    await db.orders.update(order.id, { idempotencyKey: order.idempotencyKey });
  }

  return fetch('https://api.n4-payments.com.br/api/v1/transactions', {
    method: 'POST',
    headers: {
      'X-N4-Idempotency-Key': order.idempotencyKey,
      Authorization: `Basic ${auth}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      amount: order.amount,
      paymentMethod: 'pix',
      customer: order.customer,
      externalRef: order.id,
      postbackUrl: `${HOST}/webhooks/n4`,
    }),
  });
}
```

This way every retry uses the same key until success is confirmed; once the postback lands you can
clear the key (or just leave it — it'll expire in 24h).
